import { Sparkles, Home, Zap, Car, Monitor, Leaf, Shield, Snowflake, ArrowRight, Clock, MapPin, BadgeCheck, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useBusinessMode, businessModeContent } from "@/hooks/use-business-mode";
import { Link } from "react-router-dom";
import gartenHero from "@/assets/garten-hero.jpg.asset.json";
import hausmeisterHero from "@/assets/hausmeister-hero.jpg.asset.json";
import reinigungHero from "@/assets/reinigung-hero.png.asset.json";

const miniIcons = [
  { Icon: Home, label: "Hausmeister" },
  { Icon: Zap, label: "Elektro" },
  { Icon: Car, label: "Car Detailing" },
  { Icon: Monitor, label: "IT" },
  { Icon: Leaf, label: "Garten" },
  { Icon: Shield, label: "Security" },
  { Icon: Snowflake, label: "Winterdienst" },
  { Icon: Sparkles, label: "Reinigung" },
];

const HeroSection = () => {
  const { mode } = useBusinessMode();
  const content = businessModeContent[mode];
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="about" className="relative pt-20 pb-12 md:pt-28 md:pb-24 overflow-hidden bg-gradient-to-b from-mint-light/40 via-background to-peach-light/30">
      <div className="container mx-auto px-4 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            {/* Bento grid */}
            <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4 md:gap-5 max-w-6xl mx-auto">

              {/* Headline card */}
              <div className="md:col-span-6 lg:col-span-7 lg:row-span-2 glass-card rounded-2xl md:rounded-3xl p-5 sm:p-7 md:p-10 flex flex-col justify-center animate-fade-up">
                <span className="inline-flex items-center gap-2 self-start text-xs md:text-sm font-semibold text-primary-foreground gradient-mint rounded-full px-3 md:px-4 py-1.5 mb-4 md:mb-6 shadow-soft">
                  <Sparkles className="w-3.5 h-3.5" />
                  Koordinationsservice · Hanau & Rhein-Main
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-5 leading-tight">
                  {content.heroHeadline}{" "}
                  <span className="text-primary">{content.heroHeadlineAccent}</span>
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-5 md:mb-8 max-w-xl">
                  {content.heroSubline}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={scrollToContact}
                    size="lg"
                    className="gradient-mint text-primary-foreground font-semibold px-7 shadow-glow hover:scale-105 transition-transform"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    {content.ctaLabel}
                  </Button>
                  <Button asChild variant="outline" size="lg" className="rounded-xl border-border/60 bg-card/60 backdrop-blur-sm">
                    <Link to="/dienstleistungen">
                      Leistungen ansehen
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="destructive" className="rounded-xl font-semibold shadow-soft">
                    <a href="tel:+4915565412425" aria-label="Elektrotechnik Notdienst anrufen">
                      <Zap className="mr-2 h-5 w-5" />
                      Elektro Notdienst
                    </a>
                  </Button>
                </div>
              </div>

              {/* Photo card */}
              <div className="md:col-span-6 lg:col-span-5 relative rounded-2xl md:rounded-3xl overflow-hidden shadow-card aspect-[16/9] sm:aspect-[16/8] lg:aspect-auto lg:min-h-0 animate-fade-up animation-delay-100 group">
                <img
                  src={gartenHero.url}
                  alt="Gepflegter Garten in Hanau – koordiniert von ManageMatch"
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 inline-flex items-center gap-2 text-xs font-semibold text-primary-foreground bg-card/80 backdrop-blur-md rounded-full px-3 py-1.5">
                  <BadgeCheck className="w-3.5 h-3.5 text-primary" />
                  Geprüfte Fachkräfte
                </span>
              </div>

              {/* Stat: Reaktionszeit */}
              <div className="md:col-span-3 lg:col-span-3 glass-card rounded-2xl md:rounded-3xl p-4 md:p-6 flex items-center md:items-start md:flex-col gap-4 md:gap-0 md:justify-between animate-fade-up animation-delay-200">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl gradient-peach flex items-center justify-center md:mb-4 shrink-0">
                  <Clock className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground">24–48h</p>
                  <p className="text-sm text-muted-foreground">schnelle Umsetzung nach Anfrage</p>
                </div>
              </div>

              {/* Stat: Region */}
              <div className="md:col-span-3 lg:col-span-2 glass-card rounded-2xl md:rounded-3xl p-4 md:p-6 flex items-center md:items-start md:flex-col gap-4 md:gap-0 md:justify-between animate-fade-up animation-delay-300">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl gradient-mint flex items-center justify-center md:mb-4 shrink-0">
                  <MapPin className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground">7 Tage</p>
                  <p className="text-sm text-muted-foreground">erreichbar, 08–20 Uhr</p>
                </div>
              </div>

              {/* Photo card 2 */}
              <div className="hidden lg:block lg:col-span-3 relative rounded-3xl overflow-hidden shadow-card animate-fade-up animation-delay-300 group">
                <img
                  src={reinigungHero.url}
                  alt="Professionelle Reinigung – Ergebnis in Hanau"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" />
              </div>

              {/* Services strip */}
              <div className="md:col-span-6 lg:col-span-8 glass-card rounded-2xl md:rounded-3xl p-4 md:p-6 animate-fade-up animation-delay-400">
                <p className="text-sm font-semibold text-muted-foreground mb-3 md:mb-4">Alle Bereiche aus einer Hand</p>
                <div className="flex flex-wrap gap-2 md:gap-3">
                  {miniIcons.map(({ Icon, label }, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 rounded-xl bg-accent/60 border border-border/40 px-2.5 py-1.5 md:px-3 md:py-2 hover:bg-accent hover:scale-105 transition-all"
                    >
                      <Icon className="w-4 h-4 text-primary" />
                      <span className="text-xs font-medium text-foreground">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact quick card */}
              <div className="md:col-span-6 lg:col-span-4 rounded-2xl md:rounded-3xl p-4 md:p-6 gradient-peach shadow-card flex items-start gap-4 md:block animate-fade-up animation-delay-500">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-card/70 backdrop-blur-sm flex items-center justify-center md:mb-4 shrink-0">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-bold text-primary-foreground mb-1">Kostenlose Beratung</p>
                  <p className="text-sm text-primary-foreground/80 mb-4">Wir planen, koordinieren und steuern – Sie lehnen sich zurück.</p>
                  <a
                    href="tel:+4915565412425"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary-foreground underline underline-offset-4 hover:opacity-80 transition-opacity"
                  >
                    +49 155 65412425
                  </a>
                </div>
              </div>

            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Curved divider */}
      <div className="absolute bottom-0 left-0 right-0 h-10 md:h-24 bg-muted curved-section-top" />
    </section>
  );
};

export default HeroSection;
